import { useRef, useState, type ReactNode } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import { ChildPicker } from "../_shared/child-picker";
import {
  IconCheck,
  IconDocument,
  IconPlus,
  IconWarning,
} from "../_shared/icons";
import { useDemo } from "../_shared/demo.provider";
import { getChildFromUrl } from "../_shared/router";
import { children, type Child, type DocumentRecord } from "../_data/mock";

const statusBadge: Record<
  DocumentRecord["status"],
  { label: string; class: string }
> = {
  received: { label: "Reçu", class: "bg-emerald-100 text-emerald-700" },
  missing: { label: "À envoyer", class: "bg-red-100 text-red-700" },
};

const statusIcon: Record<DocumentRecord["status"], ReactNode> = {
  received: <IconCheck width={18} height={18} strokeWidth={2.5} />,
  missing: <IconWarning width={18} height={18} />,
};

const statusIconBg: Record<DocumentRecord["status"], string> = {
  received: "bg-emerald-100 text-emerald-600",
  missing: "bg-red-100 text-red-600",
};

export function DocumentsPage() {
  const { documents, markDocumentReceived } = useDemo();
  const [selectedChildId, setSelectedChildId] = useState<Child["id"]>(() => {
    const fromUrl = getChildFromUrl();
    if (fromUrl && children.some((c) => c.id === fromUrl)) return fromUrl;
    return children[0]?.id ?? "jeanne";
  });

  const visible = documents.filter((d) => d.childId === selectedChildId);
  const transmitted = visible.filter((d) => d.status === "received").length;
  const missing = visible.length - transmitted;

  const sorted = [...visible].sort((a, b) => {
    if (a.status === b.status) return 0;
    return a.status === "missing" ? -1 : 1;
  });

  return (
    <>
      <SectionTitle title="Documents" />
      <PageShell>
        <Stack>
          <ChildPicker value={selectedChildId} onChange={setSelectedChildId} />
          <Card title="Vue d'ensemble">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-[var(--surface-2)] p-2.5">
                <p className="text-[20px] font-bold text-[var(--text-1)]">
                  {visible.length}
                </p>
                <p className="text-[11px] font-semibold text-[var(--text-2)]">
                  Total
                </p>
              </div>
              <div className="rounded-xl bg-emerald-50 p-2.5">
                <p className="text-[20px] font-bold text-emerald-700">
                  {transmitted}
                </p>
                <p className="text-[11px] font-semibold text-emerald-700">
                  Transmis
                </p>
              </div>
              <div className="rounded-xl bg-red-50 p-2.5">
                <p className="text-[20px] font-bold text-red-700">{missing}</p>
                <p className="text-[11px] font-semibold text-red-700">
                  Manquants
                </p>
              </div>
            </div>
          </Card>

          <ul className="space-y-2.5">
            {sorted.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onUpload={() => markDocumentReceived(d.id)}
              />
            ))}
          </ul>
        </Stack>
      </PageShell>
    </>
  );
}

function DocumentRow({
  doc,
  onUpload,
}: {
  doc: DocumentRecord;
  onUpload: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onPick = () => inputRef.current?.click();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files?.length ?? 0) > 0) onUpload();
    e.target.value = "";
  };

  return (
    <li className="flex items-center gap-3 rounded-2xl bg-[var(--surface-1)] p-3.5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] ring-1 ring-[var(--border-1)]">
      <span
        className={`inline-flex size-10 shrink-0 items-center justify-center rounded-xl ${statusIconBg[doc.status]}`}
      >
        {statusIcon[doc.status]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold text-[var(--text-1)]">
          {doc.label}
        </p>
        <p className="mt-0.5 truncate text-[12px] text-[var(--text-2)]">
          {doc.detail}
        </p>
      </div>
      {doc.status === "missing" ? (
        <>
          <button
            type="button"
            onClick={onPick}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[var(--sf-primary)] px-3.5 text-[12.5px] font-semibold text-white shadow-sm transition active:scale-95"
          >
            <IconPlus width={14} height={14} strokeWidth={2.5} />
            Envoyer
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onChange}
            className="hidden"
          />
        </>
      ) : (
        <span
          className={`inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-[11px] font-semibold ${statusBadge[doc.status].class}`}
        >
          <IconDocument width={12} height={12} />
          {statusBadge[doc.status].label}
        </span>
      )}
    </li>
  );
}
